import { Heading1, Heading2 } from 'components/shared/Typography.styled';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  screen,
  spacing
} from 'theme';
import { getPublicPath } from 'utils/paths';

export const StyledContainer = styled.div`
  max-width: ${container('xlMax')};
  padding: ${spacing('xl4')} ${spacing('md')} ${spacing('md')};
  margin: auto;
`;

export const StyledHero = styled.div`
  font-size: ${fontSize('md')};
  line-height: 1.5;
  color: var(--headingColor);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${spacing('md')};

  @media (min-width: ${screen('md')}) {
    flex-direction: row;
    font-size: ${fontSize('lg')};
    line-height: 1.4;
    padding-top: ${spacing('xl3')};
    padding-bottom: ${spacing('xl3')};
  }

  @media (min-width: ${screen('xl')}) {
    padding-top: ${spacing('xl5')};
  }
`;

export const StyledHeroImage = styled.img`
  @media (min-width: ${screen('md')}) {
    max-width: 25rem;
  }
  @media (min-width: ${screen('xl')}) {
    max-width: 33rem;
  }
`;

export const StyledHeroTitle = styled(Heading1)`
  max-width: 47rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing('xs')};
  font-size: ${fontSize('xl4')};
  font-family: ${fontFamily('sans')};

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('xl6')};
    margin-bottom: ${spacing('lg')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl9')};
  }

  .pretitle {
    font-family: ${fontFamily('sans')};
    font-size: ${fontSize('lg')};
    line-height: 1;
    text-transform: uppercase;
    font-weight: ${fontWeight('black')};
    letter-spacing: 0.35em;

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl')};
    }

    @media (min-width: ${screen('xl')}) {
      font-size: ${fontSize('xl3')};
    }
  }

  .year {
    font-family: inherit;
    font-size: inherit;
    letter-spacing: 0;
    display: inline-flex;
    flex-direction: column;

    &::after {
      content: '';
      background-image: ${() =>
        `url('${getPublicPath(
          '/images/stateSector/2024/hero-highlight.svg'
        )}')`};
      background-repeat: no-repeat;
      background-size: contain;
      height: ${spacing('xl3')};
    }

    @media (min-width: ${screen('md')}) {
      display: flex;
      font-size: ${fontSize('xl10')};
    }

    @media (min-width: ${screen('xl')}) {
      font-size: 10rem;
      &::after {
        height: ${spacing('xl4')};
      }
    }
  }
`;

export const StyledIntro = styled.div`
  max-width: ${container('md')};
  margin: auto;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};

  @media (min-width: ${screen('md')}) {
    margin-top: ${spacing('xl4')};
  }
`;

export const StyledIntroText = styled.div`
  margin-bottom: ${spacing('lg')};
  line-height: 1.5;

  &:not(.display-full-text) {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    @media (min-width: ${screen('md')}) {
      -webkit-line-clamp: 10;
    }
  }

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

export const StyledIntroTitle = styled(Heading2)`
  margin-bottom: ${spacing('md')};

  &::before {
    content: '🏃‍♂️💨';
    display: block;
  }
`;

export const StyledData = styled.div`
  max-width: ${container('xlMax')};
  margin: ${spacing('xl4')} auto;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};
`;

export const StyledDataTitle = styled.h4`
  font-family: ${fontFamily('sans')};
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.35em;
  margin-top: 0;
  font-weight: ${fontWeight('black')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing('xs')};
  text-align: center;

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('lg')};
  }

  &::before,
  &::after {
    content: '';
    height: 1px;
    background-color: ${color('midnight_900')};
    flex-grow: 1;
  }
`;

export const StyledDataList = styled.div`
  max-width: ${container('lg')};
  margin: auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl2')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${screen('lg')}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledDataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  font-weight: ${fontWeight('black')};
  font-size: ${fontSize('md')};
  line-height: 1.25;
  flex-direction: column;
  gap: ${spacing('xs')};
`;

export const StyledDataValue = styled.span`
  color: ${color('navy_500')};
  font-family: ${fontFamily('display')};
  font-weight: ${fontWeight('semiBold')};
  font-size: ${fontSize('xl6')};
  line-height: 1;
  flex-shrink: 0;
  width: auto;
  text-align: center;
`;

export const StyledDataContent = styled.span`
  display: flex;
  gap: ${spacing('xs')};
  flex-direction: column;
  text-align: center;

  &::before {
    content: '';
    display: block;
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: ${color('navy_200')};
    margin-left: auto;
    margin-right: auto;
  }
`;
